/**
 * Takeoff-altitude configuration for dock launches.
 *
 * Every operationally meaningful number — the presets an operator taps, the
 * regulatory ceiling, whether a waiver relaxes it, the RTH altitude — lives in
 * an org-level profile (`organizations.settings.altitude`) rather than a
 * literal, so a different agency or airspace only needs a settings change.
 *
 * Distances are stored in METERS regardless of what the operator sees; `unit`
 * decides display only. That keeps one canonical value on the wire and matches
 * the backend, which is metric everywhere.
 */

/** Display unit. Storage and transport are always meters. */
export type AltitudeUnit = 'm' | 'ft';

export const FEET_PER_METER = 3.28084;

/**
 * Hard envelope for a takeoff altitude, in meters. NOT configurable: the upper
 * bound mirrors `waypointSchema.alt` max in `src/api/docks.ts`, so an altitude
 * the UI accepts can never come back as a 400 from the API.
 *
 * Floor is 2 m, not 10: a "low hop" launch (hover just above the dock for
 * precision-landing runs, close-in inspection) is a real operation - Eden asked
 * for it on the 2026-08-23 AirScort session. 2 m clears DJI auto-takeoff's
 * fixed 1.2 m settle height; the API floor is 1 m (`.min(1)`), so nothing the
 * UI accepts can 400.
 */
export const ALTITUDE_HARD_BOUNDS = [2, 600] as const;

/** Mirrors `rth_altitude_m` bounds in `src/api/docks.ts`. */
export const RTH_HARD_BOUNDS = [10, 500] as const;

/**
 * Floor for an in-flight reposition, in meters. Lower than
 * ALTITUDE_HARD_BOUNDS[0] on purpose: that floor is a TAKEOFF minimum, and a
 * GO-TO is often a lateral move on an aircraft already hovering low for a
 * close-in inspection. Forcing those to 10 m would command a climb the operator
 * never asked for. Mirrors the `.min(1)` on `altitude_m` in `src/api/drones.ts`.
 */
export const GOTO_MIN_ALTITUDE_M = 1;

/** Slider granularity per display unit — presentation detail, not policy. */
const STEP_BY_UNIT: Record<AltitudeUnit, number> = { m: 5, ft: 10 };

/**
 * How much room the slider leaves above the highest configured altitude, so the
 * ceiling marker never sits flush against the end of the track and an operator
 * with a waiver can still reach past it.
 */
const BAND_HEADROOM = 1.25;

export const PRESET_KEYS = ['low', 'standard', 'high'] as const;
export type PresetKey = (typeof PRESET_KEYS)[number];

export const PRESET_LABEL: Record<PresetKey, string> = {
  low: 'Low',
  standard: 'Standard',
  high: 'High',
};

export type AltitudePresets = Record<PresetKey, number>;

export interface AltitudeWaiver {
  /** True when the org holds an authorization to exceed `ceiling_m`. */
  enabled: boolean;
  /** Waiver/authorization number shown to the operator. */
  reference: string | null;
}

export interface AltitudeProfile {
  unit: AltitudeUnit;
  presets: AltitudePresets;
  /** Preselected on every launch — the Option C "one click" value. */
  default_m: number;
  /** Regulatory ceiling. Advisory: exceeding it warns, it never blocks launch. */
  ceiling_m: number;
  rth_altitude_m: number;
  waiver: AltitudeWaiver;
}

/**
 * FAA-aligned starting point: 150 / 250 / 400 ft presets, 400 ft ceiling, no
 * waiver. Stored in meters at the 0.1 m granularity that makes those feet
 * values exact.
 */
export const DEFAULT_ALTITUDE_PROFILE: AltitudeProfile = {
  unit: 'ft',
  presets: { low: 45.7, standard: 76.2, high: 121.9 },
  default_m: 76.2,
  ceiling_m: 121.9,
  rth_altitude_m: 61,
  waiver: { enabled: false, reference: null },
};

/**
 * Short label for whoever sets the ceiling, keyed by the org's existing
 * `regulatory_framework` setting — so a CAAI or EASA org is never told it needs
 * a "Part 107" waiver.
 */
const CEILING_AUTHORITY: Record<string, string | null> = {
  faa_part107: 'FAA Part 107',
  caai: 'CAAI',
  easa: 'EASA',
  casa: 'CASA',
  tcca: 'TCCA',
  custom: null,
};

/** Unknown framework: name no authority rather than inventing one. */
const FALLBACK_AUTHORITY = null;

/** Round to 0.1 m — fine enough that a round number of feet survives the trip. */
function roundMeters(meters: number): number {
  return Math.round(meters * 10) / 10;
}

export function metersToFeet(meters: number): number {
  return Math.round(meters * FEET_PER_METER);
}

/**
 * Feet → meters at 0.1 m granularity. Integer meters would be too coarse: no
 * whole number of meters displays as exactly 250 ft, and 400 ft is a legal
 * threshold that must not render as 399 or 401.
 */
export function feetToMeters(feet: number): number {
  return roundMeters(feet / FEET_PER_METER);
}

/** Clamp to the hard envelope, falling back to the profile default for garbage input. */
export function clampAltitudeMeters(
  meters: number,
  profile: AltitudeProfile = DEFAULT_ALTITUDE_PROFILE,
): number {
  if (!Number.isFinite(meters)) return profile.default_m;
  const [min, max] = ALTITUDE_HARD_BOUNDS;
  return Math.min(max, Math.max(min, roundMeters(meters)));
}

/** Meters → the whole number shown in the operator's chosen unit. */
export function toDisplay(meters: number, unit: AltitudeUnit): number {
  return unit === 'ft' ? metersToFeet(meters) : Math.round(meters);
}

/** A number the operator typed or dragged, in their unit → canonical meters. */
export function fromDisplay(value: number, unit: AltitudeUnit): number {
  return unit === 'ft' ? feetToMeters(value) : Math.round(value);
}

export function formatAltitude(meters: number, unit: AltitudeUnit): string {
  return `${toDisplay(meters, unit)} ${unit}`;
}

/**
 * The widest whole display value that still converts back INSIDE a meter
 * bound. Rounding display values to the nearest integer can overshoot the
 * hard envelope in feet: 600 m displays as 1969 ft, which converts back to
 * 600.2 m — and the API rejects it with a 400; 1 m displays as 3 ft, which
 * converts back to 0.9 m, below the go-to minimum. A band must only ever
 * contain values the API will accept, so its edges are computed round-trip.
 */
function displayFloorOf(meters: number, unit: AltitudeUnit): number {
  const d = toDisplay(meters, unit);
  return fromDisplay(d, unit) > meters ? d - 1 : d;
}

function displayCeilOf(meters: number, unit: AltitudeUnit): number {
  const d = toDisplay(meters, unit);
  return fromDisplay(d, unit) < meters ? d + 1 : d;
}

export interface AltitudeBand {
  /** Bounds shared by the slider and the numeric field, already in `unit`. */
  min: number;
  max: number;
  unit: AltitudeUnit;
}

/**
 * The operational band the control covers, derived from the profile rather than
 * fixed: wide enough to reach every preset and past the ceiling, narrow enough
 * that the whole track is altitude an operator actually flies.
 *
 * Both the slider and the numeric field share this band so they can never show
 * different values for the same altitude — raising the ceiling or the `high`
 * preset is what widens it, not a separate setting.
 */
export function sliderBand(profile: AltitudeProfile, coverMeters?: number): AltitudeBand {
  const { unit } = profile;
  const step = STEP_BY_UNIT[unit];
  const topMeters = Math.max(
    profile.presets.high,
    profile.ceiling_m,
    profile.default_m,
  );
  // A waiver authorizes flight above the ceiling, so the control has to be able
  // to reach there. Without this the waiver setting would be decorative: the
  // band tops out ~25% over the ceiling and an org cleared for 800 ft could
  // only get there by editing the ceiling it was cleared to exceed.
  const bandTopMeters = profile.waiver.enabled
    ? ALTITUDE_HARD_BOUNDS[1]
    : Math.min(ALTITUDE_HARD_BOUNDS[1], Math.round(topMeters * BAND_HEADROOM));

  // The bottom is the hard minimum exactly — never rounded up onto a step grid.
  // Rounding it up would push a configured altitude below the band, and a value
  // outside the band is precisely where the slider and the field start showing
  // different numbers for the same altitude.
  const bandMin = toDisplay(ALTITUDE_HARD_BOUNDS[0], unit);
  // The top rounds up to a round number for legibility, then caps at the hard
  // envelope — rounding up must never open an altitude the API would reject.
  const hardMax = Math.floor(toDisplay(ALTITUDE_HARD_BOUNDS[1], unit) / step) * step;
  let max = Math.min(hardMax, Math.ceil(toDisplay(bandTopMeters, unit) / step) * step);
  let min = bandMin;

  // A value the control is ALREADY HOLDING has to be inside the band.
  //
  // Outside it, `clampToBand` pins what the slider and the field display while
  // the caller still holds — and sends — the original: the control shows one
  // altitude and the aircraft flies another. That is the worst thing this module
  // could do, and it is reachable in practice, because the COP seeds a GO-TO
  // from the aircraft's live altitude, which owes nothing to the org's presets.
  //
  // Widening here is always safe: it is capped at the hard envelope, so it can
  // never open an altitude the API would reject. Unlike the derived bounds
  // above, these two are NOT snapped to the step grid — snapping would round the
  // held value away and reintroduce the divergence this exists to remove.
  if (coverMeters !== undefined && Number.isFinite(coverMeters)) {
    const cover = toDisplay(coverMeters, unit);
    // Round-trip-safe caps: toDisplay rounds to NEAREST, so the naive cap
    // (toDisplay(600 m) = 1969 ft) converts back to 600.2 m and the API 400s
    // the very value this band said was fine. Same at the bottom: 3 ft is
    // 0.9 m, below the go-to minimum.
    if (cover > max) max = Math.min(displayFloorOf(ALTITUDE_HARD_BOUNDS[1], unit), cover);
    if (cover < min) min = Math.max(displayCeilOf(GOTO_MIN_ALTITUDE_M, unit), cover);
  }

  return { min, max: Math.max(min + step, max), unit };
}

/**
 * Hold a display-unit value inside the band, at whole-unit precision.
 *
 * Both controls clamp and neither snaps to a grid: a grid would silently move
 * an operator's exact entry, and any value off-grid is one the browser would
 * sanitize on the range input but not in the field — the two would then
 * disagree on screen about the same altitude.
 */
export function clampToBand(value: number, band: AltitudeBand): number {
  if (!Number.isFinite(value)) return band.min;
  return Math.min(band.max, Math.max(band.min, Math.round(value)));
}

export type CeilingStatus = 'ok' | 'waivered' | 'over';

export interface CeilingNotice {
  status: CeilingStatus;
  /** Null when the altitude is within the ceiling and there's nothing to say. */
  message: string | null;
}

/**
 * Whether the chosen altitude clears the configured ceiling. Advisory by
 * design — an operator may hold authority the platform doesn't know about, so
 * this never blocks a launch, it only states the situation.
 *
 * IT IS NO LONGER THE ONLY GATE. Since #241 the server enforces the same rule
 * on the altitude of a `takeoff` or `goto` flight command
 * (`evaluateAltitudeCeiling` in src/lib/altitude-profile.ts), and refuses
 * `over` — this function's exact sentence comes back as a 422 with
 * `code: 'altitude_ceiling'`. `waivered` is still dispatched, there as here.
 * The two must stay word-for-word identical: an operator warned in one
 * vocabulary must not be refused in another.
 */
export function evaluateCeiling(
  meters: number,
  profile: AltitudeProfile,
  authority: string | null = FALLBACK_AUTHORITY,
): CeilingNotice {
  if (meters <= profile.ceiling_m) return { status: 'ok', message: null };

  const limit = `${formatAltitude(profile.ceiling_m, profile.unit)}${authority ? ` ${authority}` : ''} ceiling`;
  if (profile.waiver.enabled) {
    const reference = profile.waiver.reference?.trim();
    return {
      status: 'waivered',
      message: `Above the ${limit} — authorized under waiver${reference ? ` ${reference}` : ''}.`,
    };
  }
  return {
    status: 'over',
    message: `Above the ${limit} — no waiver on file for this organization.`,
  };
}

export type StationAltitudeStatus = 'ok' | 'off_station';

export interface StationAltitudeNotice {
  status: StationAltitudeStatus;
  /**
   * The headline, and STABLE while the operator moves the altitude — every number in it comes
   * from the band, not from the current value.
   *
   * That is what makes it safe inside the card's `aria-live` region. The ceiling notice could
   * sit there because its string never varies once you are over; an earlier version of this one
   * led with the changing gap, so dragging the slider from 66 ft to 400 ft rewrote the live
   * region on every step and announced a stream of "34 ft off… 39 ft off…" over a control the
   * operator was actively using — and each rewrite can pre-empt the ceiling message sharing it.
   *
   * Null when the altitude is inside the band and there is nothing to say.
   */
  message: string | null;
  /**
   * How far off, in the operator's unit. VARIES with the altitude, so it belongs OUTSIDE the
   * live region (`aria-hidden`) — it is useful to look at, not to have read aloud thirty times.
   */
  gap: string | null;
  /** The constant consequence, for a muted second line. Kept out of `message` to keep it short. */
  consequence: string | null;
  /** Metres outside the band. Null when inside it. */
  offByM: number | null;
}

/** The band a covered mission's station will accept, as the server reported it. */
export interface StationAltitudeBand {
  /** `mission_coverage.station_altitude_m` for the selected mission. */
  stationAltitudeM: number;
  /**
   * The ± tolerance, in metres, READ FROM `GET /api/coverage/:missionId` and never written
   * here (#469 D3). A literal in this file would let the card bless an altitude the server's
   * gate refuses the moment either side moved — the same class of bug as #469 itself — and
   * importing the server's constant is how `main` has gone red invisibly before.
   */
  toleranceM: number;
}

/**
 * Does this altitude satisfy the coverage station the selected mission is meant to hold?
 *
 * The launch-card half of #469. `evaluateCeiling` above answers "is this altitude ALLOWED";
 * this answers "will it WORK" — a covered mission launched outside the band flies the whole
 * route correctly and then sits at `pending_station` for ever, and the operator's only signal
 * used to arrive minutes later, in the air, as a tracker refusal.
 *
 * DELIBERATELY WORDED DIFFERENTLY FROM THE SERVER'S ADVISORY, which is the opposite of the rule
 * `evaluateCeiling` follows. There, the client warning and the server refusal must be
 * word-for-word identical because they are the same event seen twice. Here they are two
 * different moments with two different useful answers: this one renders live under the ALT
 * control WHILE the operator is choosing a number, so it names the accepted range — the thing
 * they can act on; the server's rides the 202 after the aircraft is committed, so it names what
 * will now happen. Both name the same two numbers, which is what has to stay true.
 */
export function evaluateStationAltitude(
  meters: number,
  band: StationAltitudeBand | null,
  unit: AltitudeUnit,
): StationAltitudeNotice {
  const inBand: StationAltitudeNotice = {
    status: 'ok', message: null, gap: null, consequence: null, offByM: null,
  };
  if (!band) return inBand;
  // The tolerance is guarded alongside the two altitudes because it arrives over the wire, and a
  // non-finite one would make `offByM <= toleranceM` false — turning a malformed payload into a
  // confident warning whose accepted range reads "NaN m–NaN m".
  if (!Number.isFinite(meters)
    || !Number.isFinite(band.stationAltitudeM)
    || !Number.isFinite(band.toleranceM)) return inBand;

  const offByM = Math.abs(meters - band.stationAltitudeM);
  if (offByM <= band.toleranceM) return inBand;

  // The accepted range, not just the target. An operator reading "67 m off" still has to do the
  // arithmetic to find a number that works; "accepts 33 ft–98 ft" is the answer itself. Floored
  // at zero because a station near the ground would otherwise advertise a negative lower bound.
  //
  // ROUNDED INWARD, via the same helpers `sliderBand` uses. `formatAltitude` rounds to the
  // nearest whole display unit, which rounds a bound OUTWARD as readily as inward: a station at
  // 25.5 m ±10 m would be advertised as "accepts 16 m–36 m" while the real band ends at 35.5, so
  // an operator who types the 36 the card just offered still gets the amber warning. The
  // conclusion available at 02:00 is "the check is broken" — which is exactly the trust needed
  // to click past it. A stated bound must always be a bound that is honoured.
  const lowM = Math.max(0, band.stationAltitudeM - band.toleranceM);
  const highM = band.stationAltitudeM + band.toleranceM;
  const low = `${displayCeilOf(lowM, unit)} ${unit}`;
  const high = `${displayFloorOf(highM, unit)} ${unit}`;

  return {
    status: 'off_station',
    offByM,
    // ONE NOUN for this number, everywhere the operator meets it in the same ninety seconds —
    // "station altitude", matching the coverage panel's own `Station alt` row. It was previously
    // "coverage station" here, "off-station" on the button and "observation height" in the
    // server's toast, leaving the operator to work out that three names meant one number.
    message: `Off station altitude — accepts ${low}–${high} (station ${formatAltitude(band.stationAltitudeM, unit)}).`,
    gap: `${formatAltitude(offByM, unit)} off`,
    // Names a badge the operator actually sees on the mission, rather than an abstraction.
    consequence: 'The mission will fly its route but never reach COVERED.',
  };
}

function sanitizeNumber(
  value: unknown,
  fallback: number,
  [min, max]: readonly [number, number],
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, roundMeters(value)));
}

function sanitizePresets(value: unknown): AltitudePresets {
  const fallback = DEFAULT_ALTITUDE_PROFILE.presets;
  if (!value || typeof value !== 'object') return fallback;

  const raw = value as Record<string, unknown>;
  const presets = {
    low: sanitizeNumber(raw.low, fallback.low, ALTITUDE_HARD_BOUNDS),
    standard: sanitizeNumber(raw.standard, fallback.standard, ALTITUDE_HARD_BOUNDS),
    high: sanitizeNumber(raw.high, fallback.high, ALTITUDE_HARD_BOUNDS),
  };
  // Presets must read low → high in the UI. Rather than silently reordering
  // (which would show an operator a "Low" chip that isn't lowest), reject the
  // whole set and fall back to a known-good ladder.
  const ascending = presets.low < presets.standard && presets.standard < presets.high;
  return ascending ? presets : fallback;
}

function sanitizeWaiver(value: unknown): AltitudeWaiver {
  if (!value || typeof value !== 'object') return { ...DEFAULT_ALTITUDE_PROFILE.waiver };
  const raw = value as Record<string, unknown>;
  const reference = typeof raw.reference === 'string' && raw.reference.trim() !== ''
    ? raw.reference.trim().slice(0, 64)
    : null;
  return { enabled: raw.enabled === true, reference };
}

/**
 * Build a usable profile from whatever `organizations.settings` happens to hold.
 *
 * Deliberately total: JSONB is schemaless, an older row may predate the key
 * entirely, and a bad value must never be able to break the launch flow. Every
 * field independently falls back to the default.
 */
export function resolveAltitudeProfile(settings: unknown): AltitudeProfile {
  const raw = settings && typeof settings === 'object'
    ? (settings as Record<string, unknown>).altitude
    : undefined;
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ALTITUDE_PROFILE };

  const source = raw as Record<string, unknown>;
  const presets = sanitizePresets(source.presets);

  return {
    unit: source.unit === 'm' || source.unit === 'ft' ? source.unit : DEFAULT_ALTITUDE_PROFILE.unit,
    presets,
    default_m: sanitizeNumber(source.default_m, presets.standard, ALTITUDE_HARD_BOUNDS),
    ceiling_m: sanitizeNumber(source.ceiling_m, DEFAULT_ALTITUDE_PROFILE.ceiling_m, ALTITUDE_HARD_BOUNDS),
    rth_altitude_m: sanitizeNumber(source.rth_altitude_m, DEFAULT_ALTITUDE_PROFILE.rth_altitude_m, RTH_HARD_BOUNDS),
    waiver: sanitizeWaiver(source.waiver),
  };
}

/**
 * Label for whoever owns the ceiling, from the org's `regulatory_framework`.
 * Null when the framework is unknown or custom — better to say "the 400 ft
 * ceiling" than to attribute the limit to a regulator that isn't theirs.
 */
export function ceilingAuthorityLabel(settings: unknown): string | null {
  const framework = settings && typeof settings === 'object'
    ? (settings as Record<string, unknown>).regulatory_framework
    : undefined;
  if (typeof framework !== 'string') return FALLBACK_AUTHORITY;
  return CEILING_AUTHORITY[framework] ?? FALLBACK_AUTHORITY;
}
